import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { addTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/addTransport_Projection_reducer';
import { useDispatch } from 'react-redux';

const TransportAccessForm = ({ showForm, setShowForm, onAddTransport, projectiondataID }) => {
  const [newLocation, setNewLocation] = useState({
    type: '', 
    name: '', 
    distance: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (showForm) {
      setNewLocation({
        type: '',
        name: '',
        distance: ''
      });
    }
  }, [showForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLocation(prev => {
      if (!prev) {
        return { [name]: value };
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const validateForm = () => {
    return (
      newLocation.type &&
      newLocation.name &&
      newLocation.distance &&
      parseFloat(newLocation.distance) > 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
     
    const transport_detail = [newLocation];
    const projectid = projectiondataID;
   
    if (!validateForm()) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error('Please fill all fields correctly!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    
    dispatch(addTransportDetail({transport_detail, projectid}));

    setIsSubmitting(true);
    try {
      await onAddTransport({
        ...newLocation,
        distance: `${newLocation.distance} km`
      });
      toast.success('Transport added successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setNewLocation({
        type: '',
        name: '',
        distance: ''
      });
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to add transport. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeForm = (e) => {
    if (e.target === e.currentTarget) {
      setShowForm(false);
    }
  };

  if (!showForm) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
      onClick={closeForm}
    >
      <div
        className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-3xl relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setShowForm(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6 text-center">
          <h3 className="text-2xl font-bold text-white mb-1">Add New Transport</h3>
          <p className="text-gray-400 text-sm">Fill in the details below to add a new transport option</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-gray-300 text-sm font-medium mb-1 text-left">Transport Type</label>
            <input
              type="text"
              name="type"
              value={newLocation.type}
              onChange={handleInputChange}
              className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="e.g., Railway Station, Airport"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-gray-300 text-sm font-medium mb-1 text-left">Name/Identifier</label>
            <input
              type="text"
              name="name"
              value={newLocation.name}
              onChange={handleInputChange}
              className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="e.g., ABC Junction"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label className="text-gray-300 text-sm font-medium mb-1 text-left">Distance</label>
            <div className="relative">
              <input
                type="number"
                name="distance"
                value={newLocation.distance}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
                step="0.1"
                min="0.1"
                placeholder="e.g., 2.5, 12, 1.2"
              />
              <span className="absolute right-10 top-3 text-gray-400">km</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${isSubmitting ? 'bg-blue-600 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white flex items-center justify-center shadow-lg hover:shadow-blue-500/30`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding...
                </>
              ) : (
                'Add Transport'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransportAccessForm;

// import React, { useState, useEffect } from 'react';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { addTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/addTransport_Projection_reducer';
// import { useDispatch } from 'react-redux';

// const TransportAccessForm = ({ showForm, setShowForm, onAddTransport, projectiondataID }) => {
//   const [newLocation, setNewLocation] = useState({
//     type: '', 
//     name: '', 
//     distance: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [shake, setShake] = useState(false);
//   const dispatch = useDispatch();

//   useEffect(() => {
//     if (showForm) {
//       setNewLocation({
//         type: '',
//         name: '',
//         distance: ''
//       });
//     }
//   }, [showForm]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewLocation(prev => {
//       if (!prev) {
//         return { [name]: value };
//       }
//       return {
//         ...prev,
//         [name]: value
//       };
//     });
//   };

//   const validateForm = () => {
//     return (
//       newLocation.type &&
//       newLocation.name &&
//       newLocation.distance &&
//       parseFloat(newLocation.distance) > 0
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
     
//     const transport_detail = [newLocation];
//     const projectid = projectiondataID;
   
//     if (!validateForm()) {
//       setShake(true);
//       setTimeout(() => setShake(false), 500);
//       toast.error('Please fill all fields correctly!', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//       return;
//     }
    
//     dispatch(addTransportDetail({transport_detail, projectid}));

//     setIsSubmitting(true);
//     try {
//       await onAddTransport({
//         ...newLocation,
//         distance: `${newLocation.distance} km`
//       });
//       toast.success('Transport added successfully!', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//       setNewLocation({
//         type: '',
//         name: '',
//         distance: ''
//       });
//       setShowForm(false);
//     } catch (error) {
//       toast.error('Failed to add transport. Please try again.', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const closeForm = (e) => {
//     if (e.target === e.currentTarget) {
//       setShowForm(false);
//     }
//   };

//   if (!showForm) return null;

//   return (
//     <div
//       className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//       onClick={closeForm}
//     >
//       <div
//         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-3xl relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//         onClick={e => e.stopPropagation()}
//       >
//         <button
//           onClick={() => setShowForm(false)}
//           className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>

//         <div className="mb-6 text-center">
//           <h3 className="text-2xl font-bold text-white mb-1">Add New Transport</h3>
//           <p className="text-gray-400 text-sm">Fill in the details below to add a new transport option</p>
//         </div>

//         <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           <div className="flex flex-col">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Transport Type</label>
//             <input
//               type="text"
//               name="type"
//               value={newLocation.type}
//               onChange={handleInputChange}
//               className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//               placeholder="e.g., Railway Station, Airport"
//             />
//           </div>

//           <div className="flex flex-col">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Name/Identifier</label>
//             <input
//               type="text"
//               name="name"
//               value={newLocation.name}
//               onChange={handleInputChange}
//               className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//               placeholder="e.g., ABC Junction"
//             />
//           </div>

//           <div className="flex flex-col md:col-span-2">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Distance</label>
//             <div className="relative">
//               <input
//                 type="number"
//                 name="distance"
//                 value={newLocation.distance}
//                 onChange={handleInputChange}
//                 className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                 step="0.1"
//                 min="0.1"
//                 placeholder="e.g., 2.5, 12, 1.2"
//               />
//               <span className="absolute right-10 top-3 text-gray-400">km</span>
//             </div>
//           </div>

//           <div className="md:col-span-2">
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${isSubmitting ? 'bg-blue-600 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white flex items-center justify-center shadow-lg hover:shadow-blue-500/30`}
//             >
//               {isSubmitting ? (
//                 <>
//                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                   </svg>
//                   Adding...
//                 </>
//               ) : (
//                 'Add Transport'
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default TransportAccessForm;

// import React, { useState, useEffect } from 'react';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { addTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/addTransport_Projection_reducer';
// import { useDispatch } from 'react-redux';

// const TransportAccessForm = ({ showForm, setShowForm, onAddTransport , projectiondataID }) => {
//   const [newLocation, setNewLocation] = useState({
//     type: '', name: '', distance: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [shake, setShake] = useState(false);
//   const dispatch = useDispatch();

//   useEffect(() => {
//     // console.log(`this is project id ${projectiondataID}`)
//     if (showForm) {
//       setNewLocation({
//         type: '',
//         name: '',
//         distance: ''
//       });
//     }
//   }, [showForm]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewLocation(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const validateForm = () => {
//     return (
//       newLocation.type &&
//       newLocation.name &&
//       newLocation.distance &&
//       parseFloat(newLocation.distance) > 0
//     );
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     // console.log(`Thisis new form data ${JSON.stringify(newLocation)}`)
     
//     const transport_detail = [newLocation];
//     const projectid  = projectiondataID;
   
//     // return ;
//     if (!validateForm()) {
//       setShake(true);
//       setTimeout(() => setShake(false), 500);
//       toast.error('Please fill all fields correctly!', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//       return;
//     }
//     dispatch(addTransportDetail({transport_detail , projectid}))

//     setIsSubmitting(true);
//     try {
//       await onAddTransport({
//         ...newLocation,
//         distance: `${newLocation.distance} km`
//       });
//       toast.success('Transport added successfully!', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//       setNewLocation({
//         type: '',
//         name: '',
//         distance: ''
//       });
//       setShowForm(false);
//     } catch (error) {
//       toast.error('Failed to add transport. Please try again.', {
//         position: "top-right",
//         autoClose: 3000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         progress: undefined,
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const closeForm = (e) => {
//     if (e.target === e.currentTarget) {
//       setShowForm(false);
//     }
//   };

//   if (!showForm) return null;

//   return (
//     <div
//       className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//       onClick={closeForm}
//     >
//       <div
//         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-3xl relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//         onClick={e => e.stopPropagation()}
//       >
//         <button
//           onClick={() => setShowForm(false)}
//           className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>

//         <div className="mb-6 text-center">
//           <h3 className="text-2xl font-bold text-white mb-1">Add New Transport</h3>
//           <p className="text-gray-400 text-sm">Fill in the details below to add a new transport option</p>
//         </div>

//         <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           <div className="flex flex-col">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Transport Type</label>
//             <input
//               type="text"
//               name="type"
//               value={newLocation.type}
//               onChange={handleInputChange}
//               className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//               placeholder="e.g., Railway Station, Airport"
//             />
//           </div>

//           <div className="flex flex-col">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Name/Identifier</label>
//             <input
//               type="text"
//               name="name"
//               value={newLocation.name}
//               onChange={handleInputChange}
//               className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//               placeholder="e.g., ABC Junction"
//             />
//           </div>

//           <div className="flex flex-col md:col-span-2">
//             <label className="text-gray-300 text-sm font-medium mb-1 text-left">Distance</label>
//             <div className="relative">
//               <input
//                 type="number"
//                 name="distance"
//                 value={newLocation.distance}
//                 onChange={handleInputChange}
//                 className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                 step="0.1"
//                 min="0.1"
//                 placeholder="e.g., 2.5, 12, 1.2"
//               />
//               <span className="absolute right-10 top-3 text-gray-400">km</span>
//             </div>
//           </div>

//           <div className="md:col-span-2">
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${isSubmitting ? 'bg-blue-600 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white flex items-center justify-center shadow-lg hover:shadow-blue-500/30`}
//             >
//               {isSubmitting ? (
//                 <>
//                   <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                   </svg>
//                   Adding...
//                 </>
//               ) : (
//                 'Add Transport'
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default TransportAccessForm;



// import React, { useState } from 'react';

// const TransportAccessForm = ({ showForm, setShowForm, onAddTransport }) => {
//   const [newLocation, setNewLocation] = useState({
//     type: '',
//     name: '',
//     distance: '',
//   });

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewLocation(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     onAddTransport(newLocation);
//     setNewLocation({
//       type: '',
//       name: '',
//       distance: '',
//     });
//     setShowForm(false);
//   };

//   const closeForm = (e) => {
//     if (e.target === e.currentTarget) {
//       setShowForm(false);
//     }
//   };

//   if (!showForm) return null;

//   return (
//     <div 
//       className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
//       onClick={closeForm}
//     >
//       <div 
//         className="bg-gray-800 p-6 rounded-lg w-full max-w-md relative"
//         onClick={e => e.stopPropagation()}
//       >
//         <button 
//           onClick={() => setShowForm(false)}
//           className="absolute top-2 right-2 text-gray-400 hover:text-white"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//         <h3 className="text-lg font-semibold mb-4">Add New Transport Detail</h3>
//         <form onSubmit={handleSubmit}>
//           <div className="mb-4">
//             <label className="block text-gray-300 mb-2">Type</label>
//             <input
//               type="text"
//               name="type"
//               value={newLocation.type}
//               onChange={handleInputChange}
//               className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//               required
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-300 mb-2">Name</label>
//             <input
//               type="text"
//               name="name"
//               value={newLocation.name}
//               onChange={handleInputChange}
//               className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//               required
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-300 mb-2">Distance (in km)</label>
//             <input
//               type="number"
//               name="distance"
//               value={newLocation.distance}
//               onChange={handleInputChange}
//               className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//               step="0.1"
//               required
//             />
//           </div>
//           <button
//             type="submit"
//             className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
//           >
//             Add Transport
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default TransportAccessForm;